import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { gt } from 'ember-truth-helpers';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import HotelBudgetForecastTableLayoutSettingsDragItem from 'lumen/components/hotel/budget-forecast/table/layout-settings/drag-item';
import {
  DATA_TYPE_TRANSLATIONS,
  type DataType,
} from 'lumen/utils/constants/budget-forecast-constants';
import { localCopy } from 'tracked-toolbox';

export type TableLayoutSettings = {
  dataTypesOrder?: DataType[];
  hiddenDataTypes: DataType[];
  pinnedDataTypes?: DataType[];
  unpinnedDataTypes?: DataType[];
};

interface HotelBudgetForecastTableLayoutSettingsSignature {
  Args: {
    baseDataType: DataType;
    dataTypesOrder?: DataType[];
    hiddenDataTypes: DataType[];
    isDisabled?: boolean;
    isEditMode?: boolean;
    onConfirm: (args: TableLayoutSettings) => void;
    pinnedDataTypes?: DataType[];
    unpinnedDataTypes?: DataType[];
  };
}

export default class HotelBudgetForecastTableLayoutSettings extends Component<HotelBudgetForecastTableLayoutSettingsSignature> {
  @service declare usageAnalytics: Services['usage-analytics'];

  @localCopy('args.dataTypesOrder') declare dataTypesOrder: DataType[];
  @localCopy('args.hiddenDataTypes') declare hiddenDataTypes: DataType[];
  @localCopy('args.pinnedDataTypes') declare pinnedDataTypes: DataType[];
  @localCopy('args.unpinnedDataTypes') declare unpinnedDataTypes: DataType[];

  @action
  onApply(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onConfirm({
      dataTypesOrder: this.dataTypesOrder,
      pinnedDataTypes: this.pinnedDataTypes,
      unpinnedDataTypes: this.unpinnedDataTypes,
      hiddenDataTypes: this.hiddenDataTypes,
    });

    dropdown?.toggleDropdown();

    this.usageAnalytics.track('Table Layout Settings Applied', {
      eventTrigger: 'apply-button-clicked',
    });
  }

  @action
  onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.pinnedDataTypes = this.args.pinnedDataTypes ?? [];
    this.hiddenDataTypes = this.args.hiddenDataTypes ?? [];
    this.unpinnedDataTypes = this.args.unpinnedDataTypes ?? [];
    this.dataTypesOrder = this.args.dataTypesOrder ?? [];

    dropdown?.toggleDropdown();

    this.usageAnalytics.track('Table Layout Settings Canceled', {
      eventTrigger: 'cancel-button-clicked',
    });
  }

  @action
  onDataTypesOrderChange(items: DataType[]): void {
    this.dataTypesOrder = items;
    this.usageAnalytics.track('Rows Order Changed', {
      eventTrigger: 'rows-order-changed',
      dataTypeOrder: items.join(', '),
    });
  }

  @action
  onPinnedOrderChange(items: DataType[]): void {
    this.pinnedDataTypes = items;
    this.usageAnalytics.track('Pinned Columns Order Changed', {
      eventTrigger: 'pinned-columns-order-changed',
      dataTypeOrder: items.join(', '),
    });
  }

  @action
  onUnpinnedOrderChange(items: DataType[]): void {
    this.unpinnedDataTypes = items;
    this.usageAnalytics.track('Unpinned Columns Order Changed', {
      eventTrigger: 'unpinned-columns-order-changed',
      dataTypeOrder: items.join(', '),
    });
  }

  @action
  pinDataType(item: DataType): void {
    this.usageAnalytics.track('Columns Pinned', {
      eventTrigger: 'pinned-button-clicked',
      dataType: item,
    });
    this.pinnedDataTypes = [...this.pinnedDataTypes, item];
    this.unpinnedDataTypes = this.unpinnedDataTypes.filter(
      (type) => type !== item,
    );
  }

  @action
  toggleVisible(item: DataType): void {
    if (this.hiddenDataTypes.includes(item)) {
      this.hiddenDataTypes = this.hiddenDataTypes.filter(
        (type) => type !== item,
      );
      this.trackShowDataType(item);
    } else {
      this.hiddenDataTypes = [...this.hiddenDataTypes, item];
      this.trackHideDataType(item);
    }
  }

  @action
  trackHideDataType(item: DataType): void {
    if (this.args.isEditMode) {
      this.usageAnalytics.track('Rows Hidden', {
        eventTrigger: 'eye-button-clicked',
        dataType: item,
      });
    } else {
      this.usageAnalytics.track('Columns Hidden', {
        eventTrigger: 'eye-button-clicked',
        dataType: item,
      });
    }
  }

  @action
  trackShowDataType(item: DataType): void {
    if (this.args.isEditMode) {
      this.usageAnalytics.track('Rows Shown', {
        eventTrigger: 'eye-button-clicked',
        dataType: item,
      });
    } else {
      this.usageAnalytics.track('Columns Shown', {
        eventTrigger: 'eye-button-clicked',
        dataType: item,
      });
    }
  }

  @action
  unPinDataType(item: DataType): void {
    this.usageAnalytics.track('Columns Unpinned', {
      eventTrigger: 'unpinned-button-clicked',
      dataType: item,
    });
    this.unpinnedDataTypes = [item, ...this.unpinnedDataTypes];
    this.pinnedDataTypes = this.pinnedDataTypes.filter((type) => type !== item);
  }

  <template>
    <div class="relative">
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          class="ic-product-tour-48864127-layout-settings"
          iconBefore="sliders"
          isDisabled=@isDisabled
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".dropdown-target"
          {{! @glint-expect-error: Incorrect type }}
          @dropdownClassName={{concat
            "dropdown-target"
            " "
            (unless @isEditMode "top-[40px] right-0")
          }}
          @label={{t "budget_forecast.table_layout.title"}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard class="w-[328px]">
            <:header>
              <span class="text-300--medium">
                {{t "budget_forecast.table_layout.title"}}
              </span>
            </:header>
            <:content>
              <div class="overflow-hidden">
                {{#if @isEditMode}}
                  <p class="paragraph-300--regular">
                    {{t "budget_forecast.table_layout.edit.content"}}
                  </p>
                  <p class="text-200--medium mt-400 mb-200">
                    {{t "budget_forecast.table_layout.rows"}}
                  </p>
                  {{#let "data-type-order" as |groupName|}}
                    <ul
                      class="text-300--regular mb-400 w-full"
                      {{sortableGroup
                        onChange=this.onDataTypesOrderChange
                        groupName=groupName
                      }}
                    >
                      <li
                        class="rounded-200 border-neutral-default bg-elevation-surface mb-200 border border-solid px-200 py-200 shadow-neutral-100"
                      >
                        {{t (get DATA_TYPE_TRANSLATIONS @baseDataType)}}
                      </li>
                      {{#each this.dataTypesOrder key="@index" as |item|}}
                        <HotelBudgetForecastTableLayoutSettingsDragItem
                          @isHidden={{includes item this.hiddenDataTypes}}
                          @isPinnable={{false}}
                          @isPinned={{false}}
                          @label={{t (get DATA_TYPE_TRANSLATIONS item)}}
                          @toggleFixed={{fn this.unPinDataType item}}
                          @toggleVisible={{fn this.toggleVisible item}}
                          {{sortableItem
                            model=item
                            distance=5
                            groupName=groupName
                          }}
                        />
                      {{/each}}
                    </ul>
                  {{/let}}
                {{else}}
                  <p class="paragraph-300--regular">
                    {{t "budget_forecast.table_layout.view.content"}}
                  </p>
                  <p class="text-200--medium mt-400 mb-200">
                    {{t "budget_forecast.table_layout.pinned_columns"}}
                  </p>
                  {{#let "data-type-pinned" as |groupName|}}
                    <ul
                      class="text-300--regular relative mb-400 w-full"
                      {{sortableGroup
                        onChange=this.onPinnedOrderChange
                        groupName=groupName
                      }}
                    >
                      <li
                        class="rounded-200 border-neutral-default bg-elevation-surface mb-200 border border-solid px-200 py-200 shadow-neutral-100"
                      >
                        {{t (get DATA_TYPE_TRANSLATIONS @baseDataType)}}
                      </li>
                      {{#each this.pinnedDataTypes key="@index" as |item|}}
                        <HotelBudgetForecastTableLayoutSettingsDragItem
                          @isHidden={{includes item this.hiddenDataTypes}}
                          @isPinnable={{true}}
                          @isPinned={{true}}
                          @label={{t (get DATA_TYPE_TRANSLATIONS item)}}
                          @toggleFixed={{fn this.unPinDataType item}}
                          @toggleVisible={{fn this.toggleVisible item}}
                          {{sortableItem
                            model=item
                            distance=5
                            groupName=groupName
                          }}
                        />
                      {{/each}}
                    </ul>
                  {{/let}}
                  {{#if (gt this.unpinnedDataTypes.length 0)}}
                    <p class="text-200--medium mb-200">
                      {{t "budget_forecast.table_layout.columns"}}
                    </p>
                    {{#let "data-type-unpinned" as |groupName|}}
                      <ul
                        class="text-300--regular relative w-full"
                        {{sortableGroup
                          onChange=this.onUnpinnedOrderChange
                          groupName=groupName
                        }}
                      >
                        {{#each this.unpinnedDataTypes key="@index" as |item|}}
                          <HotelBudgetForecastTableLayoutSettingsDragItem
                            @isHidden={{includes item this.hiddenDataTypes}}
                            @isPinnable={{true}}
                            @isPinned={{false}}
                            @label={{t (get DATA_TYPE_TRANSLATIONS item)}}
                            @toggleFixed={{fn this.pinDataType item}}
                            @toggleVisible={{fn this.toggleVisible item}}
                            {{sortableItem
                              model=item
                              distance=5
                              groupName=groupName
                            }}
                          />
                        {{/each}}
                      </ul>
                    {{/let}}
                  {{/if}}
                {{/if}}
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.apply"}}
                  {{on "click" (fn this.onApply dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
